import { isCopilotIncompleteError } from '../copilot-rules';
import { publicSupabase } from '../supabase';

export interface CreatePaymentCaseParams {
  formSubmissionId: string;
  copilotSubmissionId?: string;
}

export type CreatePaymentCaseResult =
  | { status: 'ok'; paymentCaseId: string }
  | { status: 'already_paid' }
  | { status: 'copilot_incomplete' }
  | { status: 'error' };

/** Message levé par la RPC (migration 00133) quand le dossier BPI est déjà payé. */
const ALREADY_PAID_MESSAGE = 'bpi_already_paid';

/**
 * Crée un payment_case via la RPC SECURITY DEFINER — ou réutilise le dossier
 * existant du couple (produit, contact pilote) en cas de re-soumission
 * (TICKET-PAY-14, migration 00133). Seul form_submission_id (pilote) est
 * obligatoire — contact_id, product_id et montant sont résolus côté serveur
 * (source de vérité).
 *
 * Pour un BPI duo, passer copilotSubmissionId : la RPC pose le même bpi_code
 * sur les deux submissions, mais ne crée pas encore l'équipage. Le crew est
 * créé uniquement quand le payment_case passe à paid (CB IPN ou confirmation BPI).
 *
 * @returns résultat discriminé : `ok` (création OU réutilisation — le tunnel
 * s'ouvre sur le dossier retourné), `already_paid` (dossier déjà réglé, le
 * formulaire affiche un message dédié), `copilot_incomplete` (copilote déclaré
 * mais incomplet, TICKET-PRE28-02 / migration 00316 : le formulaire invite à
 * compléter ou à cocher « Je ne connais pas encore mon copilote »), `error`
 * (ne throw jamais)
 */
export async function createPaymentCase(
  params: CreatePaymentCaseParams
): Promise<CreatePaymentCaseResult> {
  try {
    const { data, error } = await publicSupabase.rpc('create_payment_case_from_submission', {
      p_form_submission_id: params.formSubmissionId,
      p_copilot_submission_id: params.copilotSubmissionId ?? null,
    });

    if (error) {
      if (error.message?.includes(ALREADY_PAID_MESSAGE)) {
        return { status: 'already_paid' };
      }
      if (isCopilotIncompleteError(error)) {
        return { status: 'copilot_incomplete' };
      }
      console.error('[BPI-PAYMENT] Erreur création payment_case:', error);
      return { status: 'error' };
    }

    if (!data) {
      console.error('[BPI-PAYMENT] RPC sans payment_case_id retourné');
      return { status: 'error' };
    }

    return { status: 'ok', paymentCaseId: data };
  } catch (error) {
    console.error('[BPI-PAYMENT] Exception création payment_case:', error);
    return { status: 'error' };
  }
}
